import { useState, useEffect, memo } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { 
  MapPinIcon,
  ClockIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilSquareIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  GlobeAltIcon,
  CalendarDaysIcon,
  LifebuoyIcon,
  PlusIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import { locationsApi, postOrdersApi, checkinsApi, supportApi } from '../services/api'
import { useAuth } from '../domains/auth/AuthContext'
import toast from 'react-hot-toast'
import DigitalSignature from '../components/DigitalSignature'
import FileViewer from '../components/FileViewer'

interface Location {
  id: string
  name: string
  address: string
  description?: string
  phoneNumber?: string
  emergencyContact?: string
  coordinates: {
    latitude: number
    longitude: number
  }
  hours?: string
}

interface PostOrder {
  id: string
  title: string
  content: string
  fileUrl?: string
  status: 'PENDING' | 'SIGNED' | 'EXPIRED'
  createdAt: string
  expiresAt: string
  signedAt?: string
  signature?: string
  location: {
    id: string
    name: string
  }
}

interface CheckInHistory {
  id: string
  checkInTime: string
  checkOutTime?: string
  duration?: string
  location: {
    name: string
  }
}

// Memoized header component to prevent re-rendering
const LocationsHeader = memo(() => (
  <div className="space-y-4 min-h-[120px]">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div className="p-3 rounded-xl bg-gradient-orange shadow-glow-sm animate-pulse-orange">
          <MapPinIcon className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-display-sm font-display text-gradient">Locations</h1>
          <p className="text-body text-gray-600 dark:text-tech-400 font-mono">
            Your assigned locations and post orders
          </p>
        </div>
      </div>
    </div>
  </div>
))

LocationsHeader.displayName = 'LocationsHeader'

// Memoized tab navigation component
const TabNavigation = memo(({ 
  activeTab, 
  onTabChange, 
  isTransitioning 
}: { 
  activeTab: 'locations' | 'postorders' | 'history'
  onTabChange: (tab: 'locations' | 'postorders' | 'history') => void
  isTransitioning: boolean
}) => {
  const getTabStyle = (tab: string) => {
    const isActive = activeTab === tab
    return `px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ease-in-out ${
      isActive
        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-glow transform scale-105'
        : 'bg-gray-200 dark:bg-tech-700 text-gray-700 dark:text-tech-300 hover:bg-gray-300 dark:hover:bg-tech-600 hover:scale-105'
    }`
  }

  return (
    <div className="flex space-x-2 h-12 items-center">
      <button
        onClick={() => onTabChange('locations')}
        className={getTabStyle('locations')}
        disabled={isTransitioning}
      >
        <MapPinIcon className="h-4 w-4 inline mr-2" />
        Locations
      </button>
      <button
        onClick={() => onTabChange('postorders')}
        className={getTabStyle('postorders')}
        disabled={isTransitioning}
      >
        <DocumentTextIcon className="h-4 w-4 inline mr-2" />
        Post Orders
      </button>
      <button
        onClick={() => onTabChange('history')}
        className={getTabStyle('history')}
        disabled={isTransitioning}
      >
        <ClockIcon className="h-4 w-4 inline mr-2" />
        History
      </button>
    </div>
  )
})

TabNavigation.displayName = 'TabNavigation'

// Clean loading spinner component
const LoadingSpinner = memo(() => (
  <div className="flex items-center justify-center py-12">
    <div className="flex flex-col items-center space-y-4">
      <div className="relative">
        <div className="w-8 h-8 border-2 border-gray-300 dark:border-tech-600 rounded-full animate-spin border-t-primary-500"></div>
      </div>
      <p className="text-sm text-gray-600 dark:text-tech-400">Loading locations...</p>
    </div>
  </div>
))

LoadingSpinner.displayName = 'LoadingSpinner'

export default function Locations() {
  const { user } = useAuth()
  const location = useLocation()
  const [locations, setLocations] = useState<Location[]>([])
  const [postOrders, setPostOrders] = useState<PostOrder[]>([])
  const [checkInHistory, setCheckInHistory] = useState<CheckInHistory[]>([])
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null)
  const [selectedPostOrder, setSelectedPostOrder] = useState<PostOrder | null>(null)
  const [activeTab, setActiveTab] = useState<'locations' | 'postorders' | 'history'>('locations')
  const [loading, setLoading] = useState(true)
  const [showSpinner, setShowSpinner] = useState(false)
  const [signing, setSigning] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [showSupportModal, setShowSupportModal] = useState(false)
  const [showSignatureModal, setShowSignatureModal] = useState(false)
  const [currentPostOrderId, setCurrentPostOrderId] = useState<string | null>(null)
  const [showFileViewer, setShowFileViewer] = useState(false)

  const {
    register: registerSupport,
    handleSubmit: handleSubmitSupport,
    reset: resetSupport,
    formState: { errors: supportErrors, isSubmitting: isSupportSubmitting }
  } = useForm<{
    subject: string
    description: string
    priority: 'LOW' | 'MEDIUM' | 'HIGH'
  }>()

  // Handle navigation state from check-in (similar to UserFlowController)
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab)
      
      // Show toast if there are pending post orders
      if (location.state.activeTab === 'postorders' && location.state.pendingPostOrders) {
        toast.success(`You have ${location.state.pendingPostOrders} pending post order${location.state.pendingPostOrders > 1 ? 's' : ''} to review.`)
      }
      
      // Clear the state to prevent it from persisting
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  useEffect(() => {
    loadData()
  }, [activeTab])

  const loadData = async () => {
    let spinnerTimer: NodeJS.Timeout | null = null
    
    try {
      setLoading(true)
      setShowSpinner(false)
      
      // Set a timer to show spinner after 2 seconds
      spinnerTimer = setTimeout(() => {
        if (loading) {
          setShowSpinner(true)
        }
      }, 2000)
      
      // Always load locations
      const locationsResponse = await locationsApi.getLocations()
      // Transform the data to match the expected interface
      const transformedLocations = (locationsResponse || []).map((location: any) => ({
        id: location.id,
        name: location.name,
        address: location.address,
        description: location.description,
        phoneNumber: location.phoneNumber,
        emergencyContact: location.emergencyContact,
        coordinates: {
          latitude: location.centerLat || 0,
          longitude: location.centerLng || 0
        },
        hours: location.hours
      }))
      setLocations(transformedLocations)

      if (activeTab === 'postorders') {
        const postOrdersResponse = await postOrdersApi.getPostOrders()
        setPostOrders(postOrdersResponse.data || [])
      } else if (activeTab === 'history') {
        const historyResponse = await checkinsApi.getHistory()
        // The API returns { checkIns: [...], pagination: {...} }
        const rawHistoryData = historyResponse?.checkIns || []
        
        // Transform the data to match the expected interface
        const historyData = rawHistoryData.map((checkIn: any) => ({
          id: checkIn.id || '',
          checkInTime: checkIn.checkInTime || '',
          checkOutTime: checkIn.checkOutTime || undefined,
          location: {
            name: checkIn.location?.name || 'Unknown Location'
          },
          duration: checkIn.checkOutTime 
            ? calculateDuration(checkIn.checkInTime, checkIn.checkOutTime)
            : undefined
        }))
        
        setCheckInHistory(historyData)
      }
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Failed to load data')
    } finally {
      if (spinnerTimer) {
        clearTimeout(spinnerTimer)
      }
      setLoading(false)
      setShowSpinner(false)
    }
  }

  const handleTabChange = (tab: 'locations' | 'postorders' | 'history') => {
    if (tab === activeTab) return
    
    setIsTransitioning(true)
    setActiveTab(tab)
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false)
    }, 300)
  }

  const calculateDuration = (checkInTime: string, checkOutTime: string) => {
    const checkIn = new Date(checkInTime)
    const checkOut = new Date(checkOutTime)
    const diff = checkOut.getTime() - checkIn.getTime()
    
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    
    return `${hours}h ${minutes}m`
  }



  const signPostOrder = async (postOrderId: string) => {
    setCurrentPostOrderId(postOrderId)
    setShowSignatureModal(true)
  }

  const handleSignatureSave = async (signatureUrl: string) => {
    if (!currentPostOrderId) return

    try {
      setSigning(true)
      
      const signatureData = {
        signatureUrl: signatureUrl
      }
      
      const response = await postOrdersApi.signPostOrder(currentPostOrderId, signatureData)
      
      if (response.data) {
        toast.success('Post order signed successfully')
        loadData() // Reload to get updated status
        setSelectedPostOrder(null)
      } else {
        toast.error(response.message || 'Failed to sign post order')
      }
    } catch (error: any) {
      console.error('Failed to sign post order:', error)
      toast.error(error.response?.data?.error || 'Failed to sign post order')
    } finally {
      setSigning(false)
      setCurrentPostOrderId(null)
    }
  }

  const handleSignatureClose = () => {
    setShowSignatureModal(false)
    setCurrentPostOrderId(null)
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Invalid date'
    }
  }

  const handleCreateSupportTicket = async (data: { subject: string; description: string; priority: 'LOW' | 'MEDIUM' | 'HIGH' }) => {
    try {
      if (!user?.currentOrganization?.id) {
        toast.error('No organization selected')
        return
      }

      // Auto-tag location assignment requests
      if (locations.length === 0) {
        const isLocationRequest = data.subject.toLowerCase().includes('location') || 
                                  data.description.toLowerCase().includes('location') ||
                                  data.description.toLowerCase().includes('assign')
        
        if (isLocationRequest) {
          data.subject = '[Location Assignment Request] ' + data.subject
          data.description = `[EMPLOYEE HAS NO LOCATION ASSIGNMENTS] - ${data.description}\n\nNote: This employee currently has no location assignments and is requesting to be assigned to locations.`
        }
      }

      await supportApi.createTicket({
        ...data,
        userEmail: user.email,
        userName: user.name,
        organizationId: user.currentOrganization.id
      })

      toast.success('Support ticket created successfully')
      setShowSupportModal(false)
      resetSupport()
    } catch (error: any) {
      console.error('Error creating support ticket:', error)
      toast.error(error.response?.data?.error || 'Failed to create support ticket')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'signed':
        return 'text-success bg-success/20 border-success/30'
      case 'pending':
        return 'text-warning bg-warning/20 border-warning/30'
      case 'expired':
        return 'text-error bg-error/20 border-error/30'
      default:
        return 'text-tech-400 bg-tech-700/20 border-tech-700/30'
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-6 pb-24 space-y-6 sm:space-y-8 animate-fade-in">
      {/* Static header - won't re-render */}
      <LocationsHeader />
      
      {/* Tab navigation - memoized */}
      <TabNavigation 
        activeTab={activeTab} 
        onTabChange={handleTabChange} 
        isTransitioning={isTransitioning} 
      />

      {/* Tab Content - Fixed height container */}
      <div className="min-h-[600px] transition-all duration-300">
        <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}>
          {loading && showSpinner ? (
            <LoadingSpinner />
          ) : activeTab === 'locations' && (
            <div className="space-y-6">
              {locations.length === 0 ? (
                <div className="premium-card p-8 text-center">
                  <div className="mb-6">
                    <ExclamationTriangleIcon className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                    <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display mb-2">No Location Assignments</h3>
                    <p className="text-body text-gray-600 dark:text-tech-400 mb-6">
                      You don't have any location assignments yet. Contact your administrator or create a support ticket to request location access.
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <button
                      onClick={() => setShowSupportModal(true)}
                      className="flex items-center space-x-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <LifebuoyIcon className="w-5 h-5" />
                      <span className="font-medium">Request Location Access</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {locations.map((location, index) => (
                    <div key={location.id} className={`premium-card p-6 animate-slide-up delay-${index * 100} transition-all duration-300`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600">
                            <MapPinIcon className="h-5 w-5 text-white" />
                          </div>
                          <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">{location.name}</h3>
                        </div>
                        <button
                          onClick={() => setSelectedLocation(location)}
                          className="p-2 rounded-lg bg-gray-100 dark:bg-tech-700 text-gray-600 dark:text-tech-300 hover:bg-gray-200 dark:hover:bg-tech-600 transition-colors"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <MapPinIcon className="w-4 h-4 text-gray-600 dark:text-tech-400" />
                          <span className="text-body-sm text-gray-700 dark:text-tech-300">{location.address}</span>
                        </div>
                        
                        {location.phoneNumber && (
                          <div className="flex items-center space-x-2">
                            <PhoneIcon className="w-4 h-4 text-gray-600 dark:text-tech-400" />
                            <span className="text-body-sm text-gray-700 dark:text-tech-300">{location.phoneNumber}</span>
                          </div>
                        )}
                        
                        {location.hours && (
                          <div className="flex items-center space-x-2">
                            <ClockIcon className="w-4 h-4 text-gray-600 dark:text-tech-400" />
                            <span className="text-body-sm text-gray-700 dark:text-tech-300">{location.hours}</span>
                          </div>
                        )}

                        {location.description && (
                          <p className="text-body-sm text-gray-600 dark:text-tech-400 bg-gray-50 dark:bg-tech-800/30 p-3 rounded-lg italic">
                            "{location.description}"
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'postorders' && (
            <div className="space-y-6">
              {postOrders.length === 0 ? (
                <div className="premium-card p-8 text-center">
                  <DocumentTextIcon className="w-16 h-16 text-gray-400 dark:text-tech-500 mx-auto mb-4" />
                  <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display mb-2">No Post Orders</h3>
                  <p className="text-body text-gray-600 dark:text-tech-400">You don't have any post orders to sign at the moment.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  {postOrders.map((postOrder, index) => (
                    <div key={postOrder.id} className={`premium-card p-6 animate-slide-up delay-${index * 100} transition-all duration-300`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-600">
                            <DocumentTextIcon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">{postOrder.title}</h3>
                            <p className="text-body-sm text-gray-600 dark:text-tech-400">{postOrder.location.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-3 py-1 rounded-full text-caption font-medium border ${getStatusColor(postOrder.status)}`}>
                            {postOrder.status}
                          </span>
                          <button
                            onClick={() => setSelectedPostOrder(postOrder)}
                            className="p-2 rounded-lg bg-gray-100 dark:bg-tech-700 text-gray-600 dark:text-tech-300 hover:bg-gray-200 dark:hover:bg-tech-600 transition-colors"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center space-x-4 text-body-sm text-gray-600 dark:text-tech-400">
                          <span>Created: {formatDate(postOrder.createdAt)}</span>
                          <span>•</span>
                          <span>Expires: {formatDate(postOrder.expiresAt)}</span>
                        </div>
                        
                        {postOrder.signedAt && (
                          <div className="flex items-center space-x-2">
                            <CheckCircleIcon className="w-4 h-4 text-success" />
                            <span className="text-body-sm text-success">Signed on {formatDate(postOrder.signedAt)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-6">
              {checkInHistory.length === 0 ? (
                <div className="premium-card p-8 text-center">
                  <ClockIcon className="w-16 h-16 text-gray-400 dark:text-tech-500 mx-auto mb-4" />
                  <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display mb-2">No Check-In History</h3>
                  <p className="text-body text-gray-600 dark:text-tech-400">Your check-in history will appear here.</p>
                </div>
              ) : (
                <div className="premium-card p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-600">
                      <CalendarDaysIcon className="h-5 w-5 text-white" />
                    </div>
                    <h2 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">Check-In History</h2>
                  </div>
                  
                  <div className="space-y-4">
                    {checkInHistory.map((checkIn, index) => (
                      <div key={checkIn.id} className={`flex items-center justify-between p-4 bg-gray-50 dark:bg-tech-800/30 rounded-lg animate-slide-up delay-${index * 50} transition-all duration-300`}>
                        <div className="flex items-center space-x-3">
                          <div className="p-2 rounded-full bg-success/20">
                            <ClockIcon className="w-4 h-4 text-success" />
                          </div>
                          <div>
                            <p className="text-body font-medium text-gray-900 dark:text-tech-50">{checkIn.location.name}</p>
                            <p className="text-body-sm text-gray-600 dark:text-tech-400">
                              {formatDate(checkIn.checkInTime)}
                              {checkIn.checkOutTime && ` - ${formatDate(checkIn.checkOutTime)}`}
                            </p>
                          </div>
                        </div>
                        {checkIn.duration && (
                          <div className="text-right">
                            <p className="text-body-sm font-medium text-gray-900 dark:text-tech-50">{checkIn.duration}</p>
                            <p className="text-caption text-gray-600 dark:text-tech-400">Duration</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Location Detail Modal */}
      {selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="premium-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600">
                  <MapPinIcon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">{selectedLocation.name}</h3>
              </div>
              <button
                onClick={() => setSelectedLocation(null)}
                className="p-2 rounded-lg bg-gray-100 dark:bg-tech-700 text-gray-600 dark:text-tech-300 hover:bg-gray-200 dark:hover:bg-tech-600 transition-colors"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-body font-medium text-gray-900 dark:text-tech-50 mb-2">Address</h4>
                <p className="text-body-sm text-gray-600 dark:text-tech-400">{selectedLocation.address}</p>
              </div>

              {selectedLocation.description && (
                <div>
                  <h4 className="text-body font-medium text-gray-900 dark:text-tech-50 mb-2">Description</h4>
                  <p className="text-body-sm text-gray-600 dark:text-tech-400">{selectedLocation.description}</p>
                </div>
              )}

              {selectedLocation.phoneNumber && (
                <div>
                  <h4 className="text-body font-medium text-gray-900 dark:text-tech-50 mb-2">Phone</h4>
                  <p className="text-body-sm text-gray-600 dark:text-tech-400">{selectedLocation.phoneNumber}</p>
                </div>
              )}

              {selectedLocation.emergencyContact && (
                <div>
                  <h4 className="text-body font-medium text-gray-900 dark:text-tech-50 mb-2">Emergency Contact</h4>
                  <p className="text-body-sm text-gray-600 dark:text-tech-400">{selectedLocation.emergencyContact}</p>
                </div>
              )}

              {selectedLocation.hours && (
                <div>
                  <h4 className="text-body font-medium text-gray-900 dark:text-tech-50 mb-2">Hours</h4>
                  <p className="text-body-sm text-gray-600 dark:text-tech-400">{selectedLocation.hours}</p>
                </div>
              )}

              {selectedLocation.coordinates && (
                <div>
                  <h4 className="text-body font-medium text-gray-900 dark:text-tech-50 mb-2">Coordinates</h4>
                  <div className="flex items-center space-x-2">
                    <p className="text-body-sm text-gray-600 dark:text-tech-400">
                      {selectedLocation.coordinates.latitude.toFixed(6)}, {selectedLocation.coordinates.longitude.toFixed(6)}
                    </p>
                    <button
                      onClick={() => {
                        const url = `https://www.google.com/maps?q=${selectedLocation.coordinates.latitude},${selectedLocation.coordinates.longitude}`
                        window.open(url, '_blank')
                      }}
                      className="p-1 rounded-lg bg-gray-100 dark:bg-tech-700 text-gray-600 dark:text-tech-300 hover:bg-gray-200 dark:hover:bg-tech-600 transition-colors"
                      title="Open in Google Maps"
                    >
                      <MapPinIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Post Order Detail Modal */}
      {selectedPostOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="premium-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-600">
                  <DocumentTextIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">{selectedPostOrder.title}</h3>
                  <p className="text-body-sm text-gray-600 dark:text-tech-400">{selectedPostOrder.location.name}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedPostOrder(null)}
                className="p-2 rounded-lg bg-gray-100 dark:bg-tech-700 text-gray-600 dark:text-tech-300 hover:bg-gray-200 dark:hover:bg-tech-600 transition-colors"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              <div className={`p-4 rounded-lg border ${getStatusColor(selectedPostOrder.status)}`}>
                <div className="flex items-center justify-between">
                  <span className="text-body font-medium">Status: {selectedPostOrder.status}</span>
                  <span className="text-body-sm">Expires: {formatDate(selectedPostOrder.expiresAt)}</span>
                </div>
              </div>

              <div>
                <h4 className="text-body font-medium text-gray-900 dark:text-tech-50 mb-3">Content</h4>
                <div className="bg-gray-50 dark:bg-tech-800/30 p-4 rounded-lg">
                  <p className="text-body-sm text-gray-700 dark:text-tech-300 whitespace-pre-wrap">{selectedPostOrder.content}</p>
                </div>
              </div>

              {/* View File Button */}
              {selectedPostOrder.fileUrl && (
                <div className="text-center">
                  <button
                    onClick={() => setShowFileViewer(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
                  >
                    <EyeIcon className="w-4 h-4" />
                    View Attached File
                  </button>
                </div>
              )}

              {selectedPostOrder.status === 'PENDING' && (
                <button
                  onClick={() => signPostOrder(selectedPostOrder.id)}
                  disabled={signing}
                  className="w-full flex items-center justify-center space-x-2 p-4 bg-gradient-to-r from-success to-accent-emerald text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  {signing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <PencilSquareIcon className="w-5 h-5" />
                      <span className="text-body-sm font-medium">Sign Post Order</span>
                    </>
                  )}
                </button>
              )}

              {selectedPostOrder.signedAt && (
                <div className="p-4 bg-success/10 border border-success/30 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="w-5 h-5 text-success" />
                    <span className="text-body font-medium text-success">Signed on {formatDate(selectedPostOrder.signedAt)}</span>
                  </div>
                  {selectedPostOrder.signature && (
                    <p className="text-body-sm text-gray-600 dark:text-tech-400 mt-2">Signature: {selectedPostOrder.signature}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Support Ticket Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-tech-900 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-tech-50">Request Location Access</h3>
                <button
                  onClick={() => setShowSupportModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-tech-300"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4 p-4 border border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800 rounded-lg">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-orange-800 dark:text-orange-200">Need Location Access?</h4>
                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                      This will create a support ticket to request location assignments from your administrator.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmitSupport(handleCreateSupportTicket)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-tech-300 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    {...registerSupport('subject', { required: 'Subject is required' })}
                    placeholder="Request Location Assignment"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-tech-800 text-gray-900 dark:text-tech-50"
                  />
                  {supportErrors.subject && (
                    <p className="text-red-500 text-sm mt-1">{supportErrors.subject.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-tech-300 mb-2">
                    Priority
                  </label>
                  <select
                    {...registerSupport('priority', { required: 'Priority is required' })}
                    defaultValue="MEDIUM"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-tech-800 text-gray-900 dark:text-tech-50"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                  {supportErrors.priority && (
                    <p className="text-red-500 text-sm mt-1">{supportErrors.priority.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-tech-300 mb-2">
                    Description
                  </label>
                  <textarea
                    {...registerSupport('description', { required: 'Description is required' })}
                    rows={4}
                    placeholder="Please assign me to locations so I can start working. I need access to [specify which locations if you know them]."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-tech-800 text-gray-900 dark:text-tech-50 resize-none"
                  />
                  {supportErrors.description && (
                    <p className="text-red-500 text-sm mt-1">{supportErrors.description.message}</p>
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowSupportModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 dark:text-tech-300 border border-gray-300 dark:border-tech-700 rounded-lg hover:bg-gray-50 dark:hover:bg-tech-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSupportSubmitting}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  >
                    {isSupportSubmitting ? 'Creating...' : 'Create Ticket'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Digital Signature Modal */}
      <DigitalSignature
        isOpen={showSignatureModal}
        onClose={handleSignatureClose}
        onSave={handleSignatureSave}
        title="Sign Post Order"
      />

      {/* File Viewer Modal */}
      {selectedPostOrder?.fileUrl && (
        <FileViewer
          isOpen={showFileViewer}
          onClose={() => setShowFileViewer(false)}
          fileUrl={selectedPostOrder.fileUrl}
          fileName={`${selectedPostOrder.title} - Post Order Document`}
          title={`Post Order: ${selectedPostOrder.title}`}
        />
      )}
    </div>
  )
}